import React, { useCallback, useEffect, useRef, useState, useMemo } from "react"
import ReactDOM from "react-dom"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import { useClickAway } from "react-use"

import { v4 as uuid } from "uuid"

import { IconCrossMedium } from "@izettle/otto"

import { KeyCodes } from "../../../enumerations"
import { useIsMobile } from "../../../hooks/use-is-mobile"
import { focusOnTheFirstElement } from "../../../utils/focus-on-the-first-element"
import { DialogFooter } from "../../dialog/dialog/footer"
import { ModalFooter } from "../modal-footer"

import {
  ModalBaseBackdrop,
  ModalBaseContainer,
  ModalBaseContent,
  ModalBaseEndFocus,
  ModalBaseHeader,
  ModalBaseHeaderRow,
  ModalBaseLabel,
  ModalBaseOverflowingContainer,
  StyledIconButton,
  StyledDividerHeader,
} from "./styles"
import { ModalBaseProps } from "./types"

type SortedChildren = {
  content: React.ReactElement[]
  footer: React.ReactElement | null
}

export const ModalBase: React.FC<ModalBaseProps> = React.memo(
  ({
    children,
    className,
    id = uuid(),
    isVisible,
    isOverflowing,
    isOutsideClickable,
    isMobileOverride,
    style,
    textCloseModal,
    textLabel,
    size,
    onClose,
    onCloseEnd,
    hideTextLabel,
  }) => {
    const [isPreAutoFocusLifecycle, setIsPreAutoFocusLifecycle] = useState(true)
    const [scrollTrigger, updateScrollTrigger] = useState<Record<string, unknown>>({})
    const isMobile = useIsMobile()
    const isMobileOverridden = isMobileOverride != null ? isMobileOverride : isMobile

    const closeButtonRef = useRef<HTMLButtonElement | null>(null)
    const openingElementRef = useRef<HTMLElement | null>(null)
    const modalRef = useRef<HTMLDivElement | null>(null)
    const refContent = useRef<HTMLDivElement | null>(null)

    const modalLabelId = `modal-label-${id}`

    /**
     * Handlers
     */

    const closeIfVisible = useCallback(() => {
      isVisible && onClose()
    }, [isVisible, onClose])

    // Close when clicking outside modal
    useClickAway(modalRef, () => {
      if (!isOutsideClickable) {
        closeIfVisible()
      }
    })

    const handleKeyboardInput = useCallback(
      (event: KeyboardEvent) => {
        if (event.keyCode === KeyCodes.Escape) {
          event.preventDefault()
          closeIfVisible()
        }

        if (event.shiftKey && event.keyCode === KeyCodes.Tab) {
          if (closeButtonRef.current === document.activeElement) {
            event.preventDefault()
          }
        }
      },
      [isVisible, closeIfVisible],
    )

    const handleEndFocus = useCallback(() => {
      closeButtonRef.current?.focus()
    }, [closeButtonRef.current])

    const handleTransitionExit = () => {
      if (openingElementRef?.current != null) {
        openingElementRef.current.focus()
      } else {
        focusOnTheFirstElement()
      }
      setIsPreAutoFocusLifecycle(true)
      onCloseEnd?.()
    }

    /**
     * Effects
     */

    useEffect(() => {
      if (isVisible) {
        setIsPreAutoFocusLifecycle(false)
      }
      window.addEventListener("keydown", handleKeyboardInput)
      return () => {
        window.removeEventListener("keydown", handleKeyboardInput)
      }
    }, [isVisible, handleKeyboardInput])

    useEffect(() => {
      openingElementRef.current = document.activeElement as HTMLElement | null
    }, [isVisible])

    /**
     * Rendering
     */

    const sortedChildren: SortedChildren = React.Children.toArray(children).reduce(
      (accumulator: SortedChildren, child: any) => {
        if (!child) {
          return accumulator
        }

        if (typeof child !== "object") {
          return {
            ...accumulator,
            content: [...accumulator.content, child],
          }
        }

        switch (child.type) {
          case ModalFooter:
          case DialogFooter:
            return {
              ...accumulator,
              footer: React.cloneElement(child, {
                isMobile,
                size,
              }),
            }
          default:
            return {
              ...accumulator,
              content: [...accumulator.content, child],
            }
        }
      },
      {
        content: [],
        footer: null,
      },
    )

    const getContentRef = useCallback(
      (ref) => {
        if (refContent.current !== ref) {
          refContent.current = ref
          updateScrollTrigger({})
        }
      },
      [children],
    )

    const renderTopDivider = useMemo(() => {
      if (refContent.current && refContent.current.scrollTop > 0) {
        return <StyledDividerHeader />
      }
    }, [scrollTrigger, refContent.current])

    const renderBottomDivider = useMemo(() => {
      if (!refContent.current) return null
      const totalScrollableHeight = refContent.current.scrollHeight
      const visibleHeight = refContent.current.clientHeight
      const isContentOverflowing = visibleHeight < totalScrollableHeight
      const scrolledDistance = refContent.current.scrollTop + visibleHeight
      const hasntReachedEnd = scrolledDistance !== totalScrollableHeight
      if (isContentOverflowing && hasntReachedEnd) {
        return <StyledDividerHeader />
      }
      return null
    }, [scrollTrigger, refContent.current])

    const renderContent = () => {
      return (
        <ModalBaseContainer
          ref={modalRef}
          size={size}
          isMobile={isMobileOverridden}
          isOverflowing={isOverflowing}
          hasFooter={Boolean(sortedChildren.footer)}
          className={className}
          style={style}
        >
          <StyledIconButton
            variant="minor"
            autoFocus
            isMobile={isMobileOverridden}
            ref={closeButtonRef}
            svgIcon={IconCrossMedium}
            onClick={closeIfVisible}
            title={textCloseModal}
          />
          <ModalBaseHeader hideTextLabel={hideTextLabel}>
            <ModalBaseHeaderRow isMobile={isMobileOverridden}>
              <ModalBaseLabel id={modalLabelId}>{textLabel}</ModalBaseLabel>
            </ModalBaseHeaderRow>
          </ModalBaseHeader>
          {renderTopDivider}
          <ModalBaseContent
            isMobile={isMobileOverridden}
            hideTextLabel={hideTextLabel}
            ref={getContentRef}
            onScroll={() => updateScrollTrigger({})}
          >
            {sortedChildren.content}
          </ModalBaseContent>
          {renderBottomDivider}
          {sortedChildren.footer}
          <ModalBaseEndFocus tabIndex={0} onFocus={handleEndFocus} />
        </ModalBaseContainer>
      )
    }

    return ReactDOM.createPortal(
      <TransitionGroup>
        {isVisible && !isPreAutoFocusLifecycle ? (
          <CSSTransition
            timeout={300}
            classNames="transition"
            onExit={handleTransitionExit}
          >
            <ModalBaseBackdrop
              isOverflowing={isOverflowing}
              isMobile={isMobileOverridden}
              role="dialog"
              aria-modal
              aria-hidden={false}
              aria-labelledby={modalLabelId}
            >
              {isOverflowing ? (
                <ModalBaseOverflowingContainer>
                  {renderContent()}
                </ModalBaseOverflowingContainer>
              ) : (
                renderContent()
              )}
            </ModalBaseBackdrop>
          </CSSTransition>
        ) : null}
      </TransitionGroup>,
      document.body,
    )
  },
  (prevProps: ModalBaseProps, nextProps: ModalBaseProps) => {
    return !prevProps.isVisible && !nextProps.isVisible
  },
)
